import type { Theme, ThemeAssetEntry } from '../theme';
import { resolveEntryUrl } from './AssetManifestClient';
import { hevcAlphaPreferredHere } from './videoAlpha';

/**
 * Per-theme asset "pack" manager — tracks which packs are downloaded
 * and ready to play, runs (and reports on) pack downloads.
 *
 * <p>Stage 2 of the install-size project. The manager is wired up
 * client-side now (and the {@link SlotUnlockSheet} unlock flow
 * triggers downloads) but every asset URL still resolves to the
 * bundled-in-IPA path. Stage 3 swaps the URL resolution to CDN +
 * Capacitor Filesystem cache at one point — every caller into this
 * manager keeps working unchanged.
 *
 * <h2>Pack model</h2>
 *
 * <p>A "pack" is everything {@link AssetLoader#loadTheme} +
 * {@link AudioBus#loadTheme} would fetch for a theme: slot symbols,
 * per-symbol win-anim webms, environment art, column background,
 * border, biome music, biome ambient. UI chrome / shared icons /
 * pick-em chest art are NOT in any pack — they ship with the install
 * because they're shared across themes.
 *
 * <p>Use {@link buildPackFromTheme} to materialise the list of
 * {@link ThemeAssetEntry} that constitute a theme's pack, then pass
 * it to {@link startDownload}.
 *
 * <h2>Initial state</h2>
 *
 * <p>Hosts seed the manager with already-installed themes via
 * {@link markReady}. Typical at-boot pattern: walk
 * {@code profile.unlockedSlotIds} and mark each as ready (we know
 * they're playable because the player has previously bought them and
 * Stage 2 still ships everything in the install — Stage 3 changes
 * this to "walk the Capacitor Filesystem cache").
 */
export class BiomePackManager {
  /** themeId → ready. A pack is "ready" once every URL has been
   *  fetched (Stage 2: into the browser cache; Stage 3: into the
   *  device's filesystem). */
  private readonly readyPacks = new Set<string>();

  /** themeId → in-flight download promise. Lets concurrent
   *  {@link startDownload} calls share work. */
  private readonly downloading = new Map<string, Promise<void>>();

  /** themeId → progress listeners for that theme's current download. */
  private readonly progressListeners = new Map<string, Set<PackProgressListener>>();

  /** Global ready listeners — fire whenever any pack flips to ready. */
  private readonly readyListeners = new Set<(themeId: string) => void>();

  /** Mark {@code themeId} as ready without running a download. Hosts
   *  call this at boot for themes they know are already installed
   *  (Stage 2: everything in {@code profile.unlockedSlotIds}; Stage 3:
   *  everything the filesystem cache has). */
  markReady(themeId: string): void {
    if (this.readyPacks.has(themeId)) return;
    this.readyPacks.add(themeId);
    this.readyListeners.forEach((l) => l(themeId));
  }

  /** Is this pack downloaded + ready to play? */
  isPackReady(themeId: string): boolean {
    return this.readyPacks.has(themeId);
  }

  /** Is a download for this pack currently in flight? */
  isDownloading(themeId: string): boolean {
    return this.downloading.has(themeId);
  }

  /** Every theme that's ready right now. Returns a fresh array. */
  readyThemeIds(): string[] {
    return Array.from(this.readyPacks);
  }

  /** Subscribe to ready-flips. Fires once per theme when its pack
   *  transitions ready=false → true (or {@link markReady} runs).
   *  Returns an unsubscribe fn. */
  onReady(listener: (themeId: string) => void): () => void {
    this.readyListeners.add(listener);
    return () => {
      this.readyListeners.delete(listener);
    };
  }

  /** Subscribe to per-theme download progress. Fires repeatedly as
   *  bytes arrive; the final event has {@code filesLoaded === filesTotal}.
   *  Returns an unsubscribe fn. */
  onProgress(themeId: string, listener: PackProgressListener): () => void {
    let listeners = this.progressListeners.get(themeId);
    if (!listeners) {
      listeners = new Set();
      this.progressListeners.set(themeId, listeners);
    }
    listeners.add(listener);
    return () => {
      this.progressListeners.get(themeId)?.delete(listener);
    };
  }

  /** Start downloading a pack. Idempotent: if a download is already
   *  in flight for {@code themeId}, returns the same promise; if the
   *  pack is already ready, returns immediately.
   *
   *  <p>Stage 2 implementation: HTTP-fetches every URL in {@code pack}
   *  (priming the browser cache). Stage 3: fetches from CDN to the
   *  device filesystem; on web, into Cache Storage. Either way the
   *  caller sees the same API.
   */
  async startDownload(themeId: string, pack: ThemeAssetEntry[]): Promise<void> {
    if (this.readyPacks.has(themeId)) return;
    const existing = this.downloading.get(themeId);
    if (existing) return existing;

    const run = this.runDownload(themeId, pack);
    this.downloading.set(themeId, run);
    try {
      await run;
      this.readyPacks.add(themeId);
      this.readyListeners.forEach((l) => l(themeId));
    } finally {
      this.downloading.delete(themeId);
    }
  }

  private async runDownload(themeId: string, pack: ThemeAssetEntry[]): Promise<void> {
    const filesTotal = pack.length;
    let filesLoaded = 0;
    let bytesLoaded = 0;
    // bytesTotal grows as Content-Length headers arrive — initially
    // unknown; we report estimates so the UI can animate the bar.
    let bytesTotal = 0;

    const fire = (): void => {
      const event: PackProgress = {
        themeId,
        bytesLoaded,
        bytesTotal,
        filesLoaded,
        filesTotal,
      };
      this.progressListeners.get(themeId)?.forEach((l) => l(event));
    };

    // Fire one initial 0% event so UIs can render a starting state.
    fire();

    await Promise.all(
      pack.map(async (entry) => {
        try {
          // Stage 3 — resolveEntryUrl returns the CDN-versioned URL
          // when the manifest knows about {@code entry.path}, the
          // bundled {@code src} when not, and null when neither can
          // resolve (CDN-only entry, manifest not loaded). Skip
          // unresolvable entries gracefully.
          // A transparent WebM's HEVC copy instead, where it's the one
          // that plays (WebKit; see AssetLoader.getVideo).
          const hevc = entry.hevc && hevcAlphaPreferredHere() ? resolveEntryUrl(entry.hevc) : null;
          const fetchUrl = hevc ?? resolveEntryUrl(entry);
          if (!fetchUrl) {
            console.warn(
              `[BiomePackManager] ${themeId} entry '${entry.alias}' ` +
                `has no resolvable URL (CDN-only + manifest unavailable)`,
            );
            filesLoaded++;
            fire();
            return;
          }
          const response = await fetch(fetchUrl);
          if (!response.ok) {
            // 4xx / 5xx — don't block the rest of the pack, just warn.
            // Stage 3 should retry transient failures; Stage 2's fetch
            // is local so this branch mostly catches misconfigured URLs.
            console.warn(
              `[BiomePackManager] ${themeId} entry '${entry.alias}' ` +
                `failed: HTTP ${response.status}`,
            );
            filesLoaded++;
            fire();
            return;
          }
          const lengthHeader = response.headers.get('Content-Length');
          const contentLength = lengthHeader ? parseInt(lengthHeader, 10) : 0;
          bytesTotal += contentLength;

          // Stream the body so progress events fire incrementally as
          // bytes arrive. If the body isn't streamable (some response
          // types), fall back to a single `.blob()` await — same
          // outcome, just no intermediate progress.
          const reader = response.body?.getReader();
          if (!reader) {
            await response.blob();
            bytesLoaded += contentLength;
          } else {
            // Read until EOF, summing per-chunk byteLength.
            while (true) {
              const { done, value } = await reader.read();
              if (done) break;
              if (value) bytesLoaded += value.byteLength;
              fire();
            }
          }
          filesLoaded++;
          fire();
        } catch (err) {
          console.warn(
            `[BiomePackManager] ${themeId} entry '${entry.alias}' fetch failed`,
            err,
          );
          filesLoaded++;
          fire();
        }
      }),
    );
  }

  /** Module-shared instance. Hosts that construct multiple Pixi
   *  apps share state through this. */
  static readonly shared = new BiomePackManager();
}

/** A per-fire snapshot of a pack download's state. {@link bytesTotal}
 *  can grow during the download as more Content-Length headers arrive,
 *  so a naive {@code bytesLoaded / bytesTotal} ratio may briefly drop
 *  — UI code should accept that and re-render based on the latest
 *  values. */
export interface PackProgress {
  themeId: string;
  bytesLoaded: number;
  bytesTotal: number;
  filesLoaded: number;
  filesTotal: number;
}

export type PackProgressListener = (progress: PackProgress) => void;

/**
 * Walk a theme's asset bundle and return a flat list of every URL the
 * theme needs to be playable. The list dedupes by alias so themes
 * that share an entry (e.g. the same SFX) don't get counted twice.
 *
 * <p>Excludes shared chrome (UI icons, pick-em chest art) under the
 * Stage 3 assumption that those ship in the install. Stage 2 still
 * has them in {@link Theme#assets#scene#uiIcons} etc.; if you want
 * to be pedantic, pass {@code includeShared: true} to include them.
 *
 * <p>Returns an empty array when {@code theme.assets} is absent (the
 * engine-bundled default/midnight/sunset themes).
 */
export function buildPackFromTheme(
  theme: Theme,
  options?: { includeShared?: boolean },
): ThemeAssetEntry[] {
  const includeShared = options?.includeShared ?? false;
  const seen = new Set<string>();
  const pack: ThemeAssetEntry[] = [];

  const visit = (entry: ThemeAssetEntry | null | undefined): void => {
    if (!entry || seen.has(entry.alias)) return;
    seen.add(entry.alias);
    pack.push(entry);
  };

  const visitMap = (
    map: Record<string, ThemeAssetEntry> | null | undefined,
  ): void => {
    if (!map) return;
    Object.values(map).forEach(visit);
  };

  // Per-theme buckets — slot symbols, win videos, wheel art, audio.
  visitMap(theme.assets?.slot);
  visitMap(theme.assets?.slotWin);
  visitMap(theme.assets?.wheel);
  visitMap(theme.assets?.audio);

  // Scene art — background, column-bg, border are per-theme; win-tier
  // illustrations + UI icons + pick-em chest are shared across themes
  // and stay in the install bundle unless the caller opts in.
  visit(theme.assets?.scene?.background);
  visit(theme.assets?.scene?.columnBackground);
  visit(theme.assets?.scene?.border);
  if (includeShared) {
    if (theme.assets?.scene?.winTiers) {
      Object.values(theme.assets.scene.winTiers).forEach(visit);
    }
    visitMap(theme.assets?.scene?.uiIcons);
    visitMap(theme.assets?.scene?.gameTextures);
    visitMap(theme.assets?.scene?.pickem);
  }

  return pack;
}
